/**
 * RBAC Plan C — Frontend route/sidebar → fine-grained permission-key map.
 *
 * This is the authoritative mapping consumed by later re-gating waves (C2-C4).
 * Each entry maps a gated route (from `src/routes/index.jsx`) or a gated sidebar
 * item (from `src/components/main/sidebar/utils/AppRoutes.jsx` and
 * `src/components/main/sidebar.component.jsx`) to the fine-grained permission
 * key(s) that govern its PRIMARY purpose.
 *
 * Keys are validated against the backend RBAC catalog (`app/rbac/catalog.ts`)
 * and the backend route→permission mapping
 * (docs/superpowers/plans/2026-06-15-rbac-planB-mapping.md).
 *
 * NOTE: This file ONLY declares the mapping. It does NOT change any gating
 * behavior — re-gating happens in C2-C4. The `// was [...]` comments record the
 * original coarse gate (Permission role-name array and/or user-`type` array).
 * `// type-only:` notes routes that legitimately remain a user-TYPE restriction
 * (no fine-grained customer/edge role gate today); a sensible read key is still
 * supplied for downstream consumers.
 */

export const ROUTE_PERMISSIONS = {
  // ---- routes/index.jsx: settings cluster ----
  // "settings/manage-users" — Manage Users (MangeRoles) — edge admin user mgmt
  manageUsers: ["edgeUsers.read"], // was ["EdgeAdmin"]

  // "settings/time-off" (SETTINGS_PATHS.TIME_OFF) — TimeOff policy settings
  timeoffSettings: ["timeoff.configure"], // was ["EdgeAdmin"]

  // SETTINGS_PATHS.TIME_OFF_COUNTRY — CountryDetailsWrapper (time-off policy/country)
  timeoffCountry: ["timeoff.configure"], // was ["EdgeAdmin"]

  // SETTINGS_PATHS.TIME_OFF_POLICY — PolicyDetailsWrapper (time-off policy)
  timeoffPolicy: ["timeoff.configure"], // was ["EdgeAdmin"]

  // "settings/company-profile" — CompanyProfile
  companyProfile: ["customers.update"], // was ["EdgeAdmin","CustomerAdmin","CustomerEmployee"]; PRD-1786 → customers.update

  // "settings" — OldSetting (generic settings landing; customer + edge)
  settings: ["customers.update"], // was ["EdgeAdmin","CustomerAdmin","CustomerEmployee"]; PRD-1786 → customers.update

  // ---- routes/index.jsx: edge / customer screens ----
  // "customers" — Customers (edge customer list)
  customers: ["customers.read"], // was type ["edge"]

  // "workforce" — Workforce (customer employee list) — previously ungated
  workforce: ["employees.read"], // PRD-1786

  // "talent-network" — TalentNetworkPage (browse talents)
  talentNetwork: ["talentNetwork.read"], // was ["EdgeAdmin","CustomerAdmin"] / type ["edge","customer"]

  // "talent-network/:id" — TalentProfilePage (talent profile detail)
  talentProfile: ["talentNetwork.read"], // was ["EdgeAdmin","CustomerAdmin"]

  // "request-a-hire" — RequestAHirePage
  requestAHire: ["talentNetwork.request_hire"], // was ["CustomerAdmin"] / type ["customer"]
  // PRD-3334 — shortlist + interview rounds (standard customers; PLG customers use /hire/*)
  shortlist: ["talentNetwork.shortlist"],
  interviewStatus: ["talentNetwork.request_interview"],

  // "payment" — Payment (PaymentNew) — customer payment methods/payments
  payment: ["payments.read"], // was ["CustomerAdmin"]

  // "paymentold" — PaymentOld (legacy payment screen)
  paymentOld: ["payments.read"], // was ["CustomerAdmin"]

  // "payment/add-payment-method" — AddPayment (manage payment method)
  addPaymentMethod: ["payments.manage_method"], // was ["CustomerAdmin"]

  // "groups" — Groups (edge admin + customer admin)
  groups: ["groups.read"], // was ["EdgeAdmin","CustomerAdmin"]

  // "documents" — Documents (employee document management)
  documents: ["employees.uploadDocuments"], // was ["employees.manage_documents"]

  // "workforcepayrolls" — Salaries (edge Finances screen)
  workforcePayrolls: ["finance.read"], // was ["EdgeAdmin"]

  // "payrollsupload" — PayrollUpload (edge payroll upload)
  payrollsUpload: ["payroll.upload"], // was ["EdgeAdmin"]

  // ---- routes/index.jsx: customer payroll screens (type-gated) ----
  // "payroll" — Payroll (PayrollV2) customer payroll information
  payroll: ["payroll.read"], // type-only: customer
  // "payroll/detail/:id" — PayrollHistoryDetail
  payrollDetail: ["payroll.read"], // type-only: customer
  // CUSTOMER_PAYROLL_FIRST_PAYMENT — FirstPaymentPayrollDetails
  payrollFirstPayment: ["payroll.read"], // type-only: customer (flag: integratedContractSigning)

  // ---- routes/index.jsx: profile (edge + customer) ----
  // "profile" — Profile / ProfileV2
  profile: ["userManagement.read"], // was ["customerUsers.read"]; type-only: edge,customer

  // ---- routes/index.jsx: employee self screens (type-only) ----
  // "paystubs" / "paystubs/:id" — EmployeePayStub(Details)
  paystubs: ["payroll.read"], // type-only: employee
  // "eprofile" — EmployeeProfile
  eprofile: [], // type-only: employee (no fine-grained permission — employees lack employees.read; type check gates it)
  // "edocuments" — EmployeeDocuments
  edocuments: ["employees.uploadDocuments"], // was ["employees.manage_documents"]; type-only: employee
  // "edge-wallet" — EmployeePayoneer (Payoneer self-connect)
  edgeWallet: ["payoneer.connect"], // type-only: employee
  // "peers" — EmployeePeersList(Revamped)
  peers: ["employees.readGroupPeers", "employees.readCustomerPeers"], // PRD-1848: employees.read no longer applies to employee type (narrowed in PRD-1460); use the peer keys the backend route also gates on
  // "company" — CompanyDetails (employee's company view)
  company: ["customers.read"], // type-only: employee
  // "timesheet" — Timesheet (clock-in)
  timesheet: ["timeTracking.read"], // was ["clockin.read"]; type-only: employee (flag: checkinCheckout)

  // ---- routes/index.jsx: time-off screens ----
  // "timeoff/leave" — LeaveTimeoff (customer manages employees' leave)
  timeoffLeave: ["timeoff.read"], // type-only: customer
  // "timeoff" — ManageTimeoff (employee own time-off)
  timeoff: ["timeoff.read"], // type-only: employee
  // "timeoff/request" — RequestTimeoff (employee submits own leave)
  timeoffRequest: ["timeoff.request"], // type-only: employee
  // "timeoff/admin" — TimeoffAdmin (edge admin time-off)
  timeoffAdmin: ["timeoff.read"], // type-only: edge
  // "details/timeoff" — LeaveDetails (customer leave detail)
  timeoffDetails: ["timeoff.read"], // type-only: customer
  // "admin/timeoff/new-activity" — EmployeeTimeoffNewActivity
  timeoffNewActivity: ["timeoff.requestOnBehalf"], // type-only: edge
  // "timeoff/admin/Utilisation-report" — TimeOffUtilisationReport
  timeoffUtilisationReport: ["timeoff.report"], // type-only: edge
  // "timeoff/admin/employee" — EmployeeTimeOffDetails
  timeoffAdminEmployee: ["timeoff.read"], // type-only: edge

  // ---- routes/index.jsx: holidays (time-off-adjacent reference data) ----
  // "upcoming" — EmployeeUpComingHolidays
  upcomingHolidays: ["timeoff.read"], // type-only: employee,customer
  // "holidays" — HolidaySchedule (flag: holidayCalendarRevamp)
  holidays: ["timeoff.read"], // type-only: employee,customer

  // ---- routes/index.jsx: misc gated screens ----
  // "feedback" — FeedbackScreen
  // was ["feedback.create"]; PRD-1460 #1 deleted feedback.create and un-gated the
  // company-feedback endpoint for any authenticated user. Empty array → guard's
  // `permissionKeys.length` is 0 → falls through to the type check (employee,customer).
  feedback: [], // type-only: employee,customer (no fine-grained permission)
  // "referrals" — ReferralPage
  referrals: ["referrals.read"], // type-only: customer
  // "edge-atlas" — EdgeAtlas (reference content)
  // PRD-1786: kept on customers.read (baseline) — Edge Atlas intentionally always visible
  edgeAtlas: ["customers.read"], // type-only: employee,customer
  // "chat" — Chat
  chat: ["communication.read"], // was []; PRD-1786 → communication.read

  // ---- sidebar gated items (permissions.includes / EdgeAdmin / CustomerAdmin) ----
  // Sidebar "finances" (edge, EdgeAdmin) → workforcepayrolls
  sidebarFinances: ["finance.read"], // was permissions.includes("EdgeAdmin")
  // Sidebar "payrolls-upload" / "Upload payroll" (edge, EdgeAdmin)
  sidebarPayrollsUpload: ["payroll.upload"], // was permissions.includes("EdgeAdmin")
  // Sidebar "groups" (edge EdgeAdmin & customer CustomerAdmin)
  sidebarGroups: ["groups.read"], // was permissions.includes("EdgeAdmin"|"CustomerAdmin")
  // Sidebar "account-settings" / "Manage users" / "Settings" (edge EdgeAdmin, customer CustomerAdmin)
  sidebarAccountSettings: ["customers.update"], // was permissions.includes("EdgeAdmin"|"CustomerAdmin"); PRD-1786 → customers.update
  // Sidebar "talent-network" (customer CustomerAdmin, flag-gated)
  sidebarTalentNetwork: ["talentNetwork.read"], // was permissions.includes("CustomerAdmin")
  // Sidebar "request-a-hire" (customer CustomerAdmin, flag-gated)
  sidebarRequestAHire: ["talentNetwork.request_hire"], // was permissions.includes("CustomerAdmin")
  // Sidebar "workforce" (customer) — employee list
  sidebarWorkforce: ["employees.read"], // PRD-1786
  // Sidebar "timeoff" (customer)
  sidebarTimeoff: ["timeoff.read"], // PRD-1786
  // Sidebar "holidays"/"holiday-schedule" (customer)
  sidebarHoliday: ["timeoff.read"], // PRD-1786
  // Sidebar "chat" (customer) — gated on the new communication permission
  sidebarChat: ["communication.read"], // PRD-1786

  // ---- Phase D (added now for completeness; screens land later) ----
  // Roles administration screen (Phase D)
  roleManagement: ["roles.read"], // future: Phase D
  // Feature catalog screen (Phase D)
  featureCatalog: ["features.read"], // future: Phase D

  // PRD-2043: read-only developer billing dashboard (billing-service data)
  billingDev: ["billing.read"],
  // ---- Group B: route-level gating for screens reachable by direct URL by ANY logged-in user ----
  // "editSalary" — EditSalary (edit an employee's compensation)
  editSalary: ["employees.manage_compensation"], // was ungated (bare element, any logged-in user)
  // "details" and its ":id" child — UserDetailsV2 (employee/talent detail view); parent gate
  // also covers the ":id" child since it renders the same component with no <Outlet/>, so the
  // child route's own render output is discarded regardless — see routes/index.jsx.
  employeeDetails: ["employees.read"], // was ungated (bare element, any logged-in user)
  // "adduser" — AddUser (add/edit an Edge or Customer user); either create or update is sufficient
  // since the same route/component serves both the "add" and "edit" flows
  addUser: ["edgeUsers.create", "edgeUsers.update"], // was ungated (bare element, any logged-in user)
  // "addemployee" — AddTalent (add/edit a customer's employee); verified edge-only reachable
  // (only navigated to from AdminWorkforce, which Workforce/index.jsx renders solely for
  // loggedInUserType === "edge")
  addEmployee: ["employees.create"], // was ungated (bare element, any logged-in user)
  // "addcustomer" — AddCustomer: NOT gated here. PersonalInfo.jsx (rendered by default on the
  // "Profile" tab of customer/details) has an unguarded Edit button that navigates here in
  // "editCustomer" mode, and customer/details is reachable by customer-type users (see
  // customerDetails below). Gating on the edge-only customers.create would lock those users out.
  // "user/details" and its ":id" child — UserDetail (Setting/MangeRoles/userDetail); verified
  // edge-only reachable (Setting/MangeRoles/ManageUsers.jsx, the only live caller, is itself only
  // rendered for loggedInUserType === "edge" via SettingsV2.jsx and the edgeUsers.read-gated
  // settings/manage-users route)
  edgeUserDetails: ["edgeUsers.read"], // was ungated (bare element, any logged-in user)
  // "customer/details" and its ":id" child — CustomerDetials (edge + customer company detail view)
  customerDetails: ["customers.read"], // was ungated (bare element, any logged-in user)
};

export default ROUTE_PERMISSIONS;
